import React, { useState, useEffect, useRef } from 'react';
import Sidebar, { SIDEBAR_W } from '../components/Sidebar';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';

// Flat blue palette — matches the redesigned pages.
const C_BG = '#CDE7F5';
const C_CARD = '#FFFFFF';
const C_BORDER = '#E3ECF5';
const C_TEXT = '#03045E';
const C_TEXT_2 = '#6B7C93';
const C_ACCENT = '#0077B6';
const C_ACCENT_2 = '#00B4D8';
const C_TINT = '#E6F1FB';

const NAILS_PER_STICK = 25;
const NAILS_PER_COIL = 300;

// Amber tint for the special editable Paddock row.
const C_AMBER_TINT = '#FAEEDA';

// Board reference data — the DEFAULT framing/coil-per-board values. These seed
// the editable, Firestore-persisted framing/coil columns on first run; null
// means "not applicable" (an empty cell). Every row is editable at runtime.
// `paddockNote` only drives the amber highlight + reminder badge on 1x4x4.
const BOARDS = [
  { label: '2x4x8', framingPerBoard: 6, coilPerBoard: null },
  { label: '1x4x4', framingPerBoard: 4, coilPerBoard: null, paddockNote: true },
  { label: '1x4x5', framingPerBoard: 5, coilPerBoard: 6 },
  { label: '1x4x6', framingPerBoard: null, coilPerBoard: 6 },
  { label: '1x4x8', framingPerBoard: null, coilPerBoard: 6 },
  { label: '6x6x8', framingPerBoard: 1, coilPerBoard: null },
  { label: '6x6x10', framingPerBoard: 1, coilPerBoard: null },
  { label: '2x4x8 cedar', framingPerBoard: 8, coilPerBoard: null },
  { label: '1x6x6', framingPerBoard: null, coilPerBoard: 9 },
  { label: '1x6x6 DE', framingPerBoard: null, coilPerBoard: 9 },
  { label: '1x6x8', framingPerBoard: null, coilPerBoard: 9 },
  { label: '4x4x7', framingPerBoard: 1, coilPerBoard: null },
  { label: '4x4x8', framingPerBoard: 1, coilPerBoard: null },
  { label: '4x4x10', framingPerBoard: 1, coilPerBoard: null },
  { label: '2x4x10', framingPerBoard: 6, coilPerBoard: null },
];

function NailCalculator({ user }) {
  const [quantities, setQuantities] = useState(
    BOARDS.reduce((acc, b) => { acc[b.label] = 0; return acc; }, {})
  );

  // Framing/coil values per board — editable on every row, persisted in Firestore
  // (settings/nailCalculator). Seeded from the hardcoded BOARDS defaults so the
  // calculator works before the doc loads / offline. A BLANK ('') means "not
  // applicable" and is excluded from the totals.
  const [framingValues, setFramingValues] = useState(() =>
    BOARDS.reduce((acc, b) => { acc[b.label] = b.framingPerBoard == null ? '' : b.framingPerBoard; return acc; }, {})
  );
  const [coilValues, setCoilValues] = useState(() =>
    BOARDS.reduce((acc, b) => { acc[b.label] = b.coilPerBoard == null ? '' : b.coilPerBoard; return acc; }, {})
  );
  const [saveStatus, setSaveStatus] = useState(''); // '' | 'saving' | 'saved' | 'error'

  // Gate persistence until the initial load settles, and skip the one state
  // update the load itself causes (so we don't re-write what we just read).
  const hydratedRef = useRef(false);
  const skipNextSaveRef = useRef(false);

  const num = (v) => {
    const n = parseFloat(v);
    return isNaN(n) ? 0 : n;
  };

  // A value contributes to the totals only when it is actually set — a blank
  // ('' / null / undefined) is "not applicable" and skipped; 0 counts as a set 0.
  const isSet = (v) => v !== '' && v !== null && v !== undefined;

  const setQty = (label, value) => {
    const n = Math.max(0, num(value));
    setQuantities(prev => ({ ...prev, [label]: value === '' ? '' : n }));
  };

  const setFraming = (label, value) =>
    setFramingValues(prev => ({ ...prev, [label]: value === '' ? '' : Math.max(0, num(value)) }));
  const setCoil = (label, value) =>
    setCoilValues(prev => ({ ...prev, [label]: value === '' ? '' : Math.max(0, num(value)) }));

  // Firestore doc shape: { framing: {label: n|null}, coil: {...}, updatedAt }.
  // Blank ('') round-trips as null so "not applicable" persists correctly.
  const buildSettingsData = () => {
    const framing = {};
    const coil = {};
    BOARDS.forEach(b => {
      framing[b.label] = framingValues[b.label] === '' ? null : num(framingValues[b.label]);
      coil[b.label] = coilValues[b.label] === '' ? null : num(coilValues[b.label]);
    });
    return { framing, coil, updatedAt: new Date().toISOString() };
  };

  // Load persisted values on mount. Missing doc → seed it from the defaults.
  // Read failure → keep the BOARDS-seeded state so it still works offline.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'settings', 'nailCalculator'));
        if (cancelled) return;
        if (snap.exists()) {
          const data = snap.data() || {};
          skipNextSaveRef.current = true;
          const apply = (mapFromDoc) => (prev) => {
            const next = { ...prev };
            BOARDS.forEach(b => {
              if (mapFromDoc && Object.prototype.hasOwnProperty.call(mapFromDoc, b.label)) {
                next[b.label] = mapFromDoc[b.label] == null ? '' : mapFromDoc[b.label];
              }
            });
            return next;
          };
          setFramingValues(apply(data.framing));
          setCoilValues(apply(data.coil));
        } else {
          await setDoc(doc(db, 'settings', 'nailCalculator'), buildSettingsData(), { merge: true });
        }
      } catch (err) {
        console.error('Nail settings load failed — using defaults:', err);
      } finally {
        if (!cancelled) hydratedRef.current = true;
      }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced write (~800ms after typing stops). Skips the initial hydrate and
  // the load-induced state settle so only real edits persist.
  useEffect(() => {
    if (!hydratedRef.current) return;
    if (skipNextSaveRef.current) { skipNextSaveRef.current = false; return; }
    setSaveStatus('saving');
    const t = setTimeout(async () => {
      try {
        await setDoc(doc(db, 'settings', 'nailCalculator'), buildSettingsData(), { merge: true });
        setSaveStatus('saved');
      } catch (err) {
        console.error('Nail settings save failed:', err);
        setSaveStatus('error');
      }
    }, 800);
    return () => clearTimeout(t);
  }, [framingValues, coilValues]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalFramingNails = BOARDS.reduce((sum, b) => {
    const per = framingValues[b.label];
    if (!isSet(per)) return sum;
    return sum + num(quantities[b.label]) * num(per);
  }, 0);

  const totalCoilNails = BOARDS.reduce((sum, b) => {
    const per = coilValues[b.label];
    if (!isSet(per)) return sum;
    return sum + num(quantities[b.label]) * num(per);
  }, 0);

  const totalSticks = totalFramingNails / NAILS_PER_STICK;
  const totalCoils = totalCoilNails / NAILS_PER_COIL;

  const handleClear = () => {
    // Only the per-calculation quantities reset; framing/coil are persisted
    // reference data and must NOT be cleared here.
    setQuantities(BOARDS.reduce((acc, b) => { acc[b.label] = 0; return acc; }, {}));
  };

  const qtyInputStyle = {
    width: 80,
    padding: '6px 10px',
    background: '#FFFFFF',
    border: `1px solid ${C_BORDER}`,
    borderRadius: 8,
    color: C_TEXT,
    fontSize: 14,
    outline: 'none',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  };

  const focusOn = (e) => { e.target.style.borderColor = C_ACCENT; };
  const focusOff = (e) => { e.target.style.borderColor = C_BORDER; };

  const thStyle = {
    textAlign: 'left',
    padding: '10px 12px',
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    color: C_TEXT,
    background: C_TINT,
    borderBottom: `1px solid ${C_BORDER}`,
  };

  const tdStyle = {
    padding: '8px 12px',
    fontSize: 14,
    color: C_TEXT,
    borderBottom: `1px solid ${C_BORDER}`,
    verticalAlign: 'middle',
  };

  return (
    <>
      <Sidebar />
      <div style={{ minHeight: '100vh', background: C_BG, marginLeft: SIDEBAR_W, fontFamily: 'Manrope, sans-serif' }}>
        <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px' }}>

          {/* Header */}
          <div style={{ marginBottom: 24 }}>
            <h1 style={{ fontSize: 26, fontWeight: 800, color: C_TEXT, margin: 0, letterSpacing: '-0.5px' }}>
              🔨 Nail Calculator
            </h1>
            <p style={{ color: C_TEXT_2, fontSize: 14, margin: '4px 0 0 0' }}>
              Enter how many boards you'll use for each type.
            </p>
            {saveStatus && (
              <p style={{
                margin: '6px 0 0 0', fontSize: 12, fontWeight: 600,
                color: saveStatus === 'error' ? '#A32D2D' : saveStatus === 'saved' ? '#0F6E56' : C_TEXT_2,
              }}>
                {saveStatus === 'saving'
                  ? 'Saving…'
                  : saveStatus === 'saved'
                    ? 'Saved ✓'
                    : 'Save failed — check your connection'}
              </p>
            )}
          </div>

          {/* Table card */}
          <div style={{ background: C_CARD, border: `1px solid ${C_BORDER}`, borderRadius: 14, overflow: 'hidden', marginBottom: 20 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>Board</th>
                  <th style={{ ...thStyle, textAlign: 'center' }}># Boards</th>
                  <th style={{ ...thStyle, textAlign: 'center' }}>Framing / board</th>
                  <th style={{ ...thStyle, textAlign: 'center' }}>Coil / board</th>
                </tr>
              </thead>
              <tbody>
                {BOARDS.map((b) => {
                  const isPaddock = b.paddockNote;
                  const rowBg = isPaddock ? C_AMBER_TINT : 'transparent';
                  return (
                    <tr key={b.label} style={{ background: rowBg }}>
                      <td style={tdStyle}>
                        <span style={{ fontWeight: 600 }}>{b.label}</span>
                        {isPaddock && (
                          <span style={{
                            marginLeft: 8, fontSize: 11, fontWeight: 700,
                            color: '#854F0B', background: '#FDF1DD',
                            padding: '2px 6px', borderRadius: 4,
                          }}>
                            Paddock: change to 5
                          </span>
                        )}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        <input
                          type="number"
                          min="0"
                          value={quantities[b.label]}
                          onChange={(e) => setQty(b.label, e.target.value)}
                          onFocus={focusOn}
                          onBlur={focusOff}
                          style={{ ...qtyInputStyle, textAlign: 'center' }}
                        />
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        <input
                          type="number"
                          min="0"
                          value={framingValues[b.label]}
                          onChange={(e) => setFraming(b.label, e.target.value)}
                          onFocus={focusOn}
                          onBlur={focusOff}
                          placeholder="—"
                          style={{ ...qtyInputStyle, width: 70, textAlign: 'center' }}
                        />
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        <input
                          type="number"
                          min="0"
                          value={coilValues[b.label]}
                          onChange={(e) => setCoil(b.label, e.target.value)}
                          onFocus={focusOn}
                          onBlur={focusOff}
                          placeholder="—"
                          style={{ ...qtyInputStyle, width: 70, textAlign: 'center' }}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Summary metric cards */}
          <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
            <div style={{
              flex: 1, minWidth: 220,
              background: C_CARD, border: `1px solid ${C_BORDER}`, borderRadius: 14,
              padding: 20, borderTop: `3px solid ${C_ACCENT}`,
            }}>
              <p style={{ margin: 0, fontSize: 12, fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', color: C_TEXT_2 }}>
                Total Sticks
              </p>
              <p style={{ margin: '6px 0 0 0', fontSize: 30, fontWeight: 800, color: C_ACCENT }}>
                {totalSticks.toFixed(1)} sticks
              </p>
              <p style={{ margin: '4px 0 0 0', fontSize: 13, color: C_TEXT_2 }}>
                {totalFramingNails} framing nails
              </p>
            </div>
            <div style={{
              flex: 1, minWidth: 220,
              background: C_CARD, border: `1px solid ${C_BORDER}`, borderRadius: 14,
              padding: 20, borderTop: `3px solid ${C_ACCENT_2}`,
            }}>
              <p style={{ margin: 0, fontSize: 12, fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', color: C_TEXT_2 }}>
                Total Coils
              </p>
              <p style={{ margin: '6px 0 0 0', fontSize: 30, fontWeight: 800, color: C_ACCENT_2 }}>
                {totalCoils.toFixed(1)} coils
              </p>
              <p style={{ margin: '4px 0 0 0', fontSize: 13, color: C_TEXT_2 }}>
                {totalCoilNails} coil nails
              </p>
            </div>
          </div>

          {/* Clear button */}
          <button
            onClick={handleClear}
            style={{
              background: 'transparent',
              border: `1px solid ${C_ACCENT}`,
              color: C_ACCENT,
              fontWeight: 700,
              fontSize: 14,
              padding: '10px 20px',
              borderRadius: 10,
              cursor: 'pointer',
              fontFamily: 'inherit',
              transition: 'background 0.2s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = C_TINT; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            Clear
          </button>

        </div>
      </div>
    </>
  );
}

export default NailCalculator;
